import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'

export interface UserProfile {
  id: string
  name: string
  email: string
  roles: string[]
  permissions: string[]
  isAdmin?: boolean
  isTeacher?: boolean
  isReception?: boolean
  avatarUrl?: string
  culture?: string
}

export interface UnreadCounters {
  pendingComments: number
  unreadFeedback: number
  unreadEventRegs: number
  unreadCampRegs: number
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserProfile | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(true)
  const counters = ref<UnreadCounters>({
    pendingComments: 0,
    unreadFeedback: 0,
    unreadEventRegs: 0,
    unreadCampRegs: 0
  })

  const isAdmin = computed(() => {
    if (!user.value) return false
    return !!user.value.isAdmin || user.value.roles.includes('SuperAdmin') || user.value.roles.includes('Admin')
  })

  const isTeacher = computed(() => {
    if (!user.value) return false
    if (isAdmin.value) return false
    return !!user.value.isTeacher || user.value.roles.includes('Teacher')
  })

  const isReception = computed(() => {
    if (!user.value) return false
    if (isAdmin.value) return false
    return !!user.value.isReception || user.value.roles.includes('Reception')
  })

  function can(...permissions: string[]): boolean {
    if (!user.value) return false
    if (isAdmin.value) return true
    return permissions.some(p => user.value?.permissions?.includes(p))
  }

  async function fetchUser() {
    isLoading.value = true
    try {
      const data = await api.get<{ user: UserProfile; counters: UnreadCounters }>('/api/admin/auth/me')
      if (data && data.user) {
        user.value = data.user
        isAuthenticated.value = true
        if (data.counters) {
          counters.value = data.counters
        }
      }
    } catch (err) {
      user.value = null
      isAuthenticated.value = false
    } finally {
      isLoading.value = false
    }
  }

  async function login(credentials: { email: string; password: string; rememberMe?: boolean }) {
    await api.post('/api/admin/auth/login', credentials)
    await fetchUser()
    return user.value
  }

  async function logout() {
    try {
      await api.post('/api/admin/auth/logout')
    } catch {
      // Ignore network errors during logout
    } finally {
      user.value = null
      isAuthenticated.value = false
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    counters,
    isAdmin,
    isTeacher,
    isReception,
    can,
    fetchUser,
    login,
    logout
  }
})
